import React, { useState, useEffect } from 'react';
import { Copy, Plus, Trash2, ToggleLeft, ToggleRight, Users, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { getClients, createClient, toggleClient, deleteClient } from '../services/api';

const ManageClients = () => {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newClientName, setNewClientName] = useState('');
    const [newClientToken, setNewClientToken] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const fetchClients = async () => {
        try {
            setLoading(true);
            const response = await getClients();
            if (response.success) {
                setClients(response.clients);
            }
        } catch (error) {
            console.error('Error fetching clients:', error);
            toast.error('Failed to fetch clients');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClients();
    }, []);

    const handleAddClient = async (e) => {
        e.preventDefault();
        if (!newClientName.trim()) {
            toast.error('Client name is required');
            return;
        }

        if (!newClientToken.trim()) {
            toast.error('S2S token is required');
            return;
        }

        try {
            setSubmitting(true);
            const response = await createClient({
                clientName: newClientName.trim(),
                token: newClientToken.trim()
            });
            if (response.success) {
                toast.success('Client created successfully!');
                setClients([response.client, ...clients]);
                setShowAddModal(false);
                setNewClientName('');
                setNewClientToken('');
            }
        } catch (error) {
            console.error('Error creating client:', error);
            toast.error(error.response?.data?.message || 'Failed to create client');
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleStatus = async (id) => {
        try {
            const response = await toggleClient(id);
            if (response.success) {
                setClients(clients.map(c =>
                    c.id === id ? { ...c, isActive: response.client.isActive } : c
                ));
                toast.success(response.message);
            }
        } catch (error) {
            console.error('Error toggling status:', error);
            toast.error('Failed to update client status');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this client? This will invalidate their S2S token.')) {
            return;
        }

        try {
            const response = await deleteClient(id);
            if (response.success) {
                setClients(clients.filter(c => c.id !== id));
                toast.success('Client deleted successfully');
            }
        } catch (error) {
            console.error('Error deleting client:', error);
            toast.error('Failed to delete client');
        }
    };

    const copyToClipboard = (token) => {
        navigator.clipboard.writeText(token);
        toast.success('Token copied to clipboard!');
    };

    const styles = {
        container: {
            padding: '24px',
            maxWidth: '1400px',
            margin: '0 auto'
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
        },
        title: {
            fontSize: '28px',
            fontWeight: '700',
            color: '#14532d',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
        },
        addButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 20px',
            background: 'linear-gradient(135deg, #15803d 0%, #22c55e 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '14px'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            background: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
        },
        th: {
            padding: '16px',
            textAlign: 'left',
            background: '#f8fafc',
            fontWeight: '600',
            color: '#475569',
            borderBottom: '2px solid #e2e8f0',
            fontSize: '14px'
        },
        td: {
            padding: '16px',
            borderBottom: '1px solid #e2e8f0',
            color: '#334155',
            fontSize: '14px'
        },
        tokenCell: {
            fontFamily: 'monospace',
            background: '#f1f5f9',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            maxWidth: '300px'
        },
        tokenText: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1
        },
        copyBtn: {
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: '#22c55e',
            padding: '4px'
        },
        badge: {
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600'
        },
        activeBadge: {
            background: '#dcfce7',
            color: '#166534'
        },
        inactiveBadge: {
            background: '#fee2e2',
            color: '#991b1b'
        },
        actionBtn: {
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s'
        },
        modal: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        },
        modalContent: {
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            width: '400px',
            maxWidth: '90%'
        },
        modalTitle: {
            fontSize: '20px',
            fontWeight: '700',
            color: '#14532d',
            marginBottom: '24px'
        },
        input: {
            width: '100%',
            padding: '12px 16px',
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '14px',
            marginBottom: '20px',
            boxSizing: 'border-box'
        },
        modalButtons: {
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
        },
        cancelBtn: {
            padding: '10px 20px',
            background: '#f1f5f9',
            color: '#475569',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            cursor: 'pointer'
        },
        submitBtn: {
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #15803d 0%, #22c55e 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            cursor: 'pointer'
        },
        emptyState: {
            textAlign: 'center',
            padding: '60px 20px',
            color: '#64748b'
        },
        refreshBtn: {
            background: 'transparent',
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            padding: '10px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>
                    <Users size={32} color="#22c55e" />
                    Manage Clients
                </h1>
                <div style={{ display: 'flex', gap: '12px' }}>
                    <button style={styles.refreshBtn} onClick={fetchClients} title="Refresh">
                        <RefreshCw size={20} color="#475569" />
                    </button>
                    <button style={styles.addButton} onClick={() => setShowAddModal(true)}>
                        <Plus size={20} />
                        Add Client
                    </button>
                </div>
            </div>

            {loading ? (
                <div style={styles.emptyState}>Loading clients...</div>
            ) : clients.length === 0 ? (
                <div style={styles.emptyState}>
                    <Users size={48} color="#cbd5e1" />
                    <h3 style={{ margin: '16px 0 8px' }}>No Clients Yet</h3>
                    <p>Add a client to generate an S2S token for API access.</p>
                </div>
            ) : (
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>Client Name</th>
                            <th style={styles.th}>S2S Token</th>
                            <th style={styles.th}>Status</th>
                            <th style={styles.th}>Usage</th>
                            <th style={styles.th}>Last Used</th>
                            <th style={styles.th}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {clients.map(client => (
                            <tr key={client.id}>
                                <td style={styles.td}>
                                    <strong>{client.clientName}</strong>
                                </td>
                                <td style={styles.td}>
                                    <div style={styles.tokenCell}>
                                        <span style={styles.tokenText}>{client.token}</span>
                                        <button style={styles.copyBtn} onClick={() => copyToClipboard(client.token)} title="Copy token">
                                            <Copy size={16} />
                                        </button>
                                    </div>
                                </td>
                                <td style={styles.td}>
                                    <span style={{
                                        ...styles.badge,
                                        ...(client.isActive ? styles.activeBadge : styles.inactiveBadge)
                                    }}>
                                        {client.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td style={styles.td}>{client.usageCount || 0} calls</td>
                                <td style={styles.td}>
                                    {client.lastUsedAt
                                        ? new Date(client.lastUsedAt).toLocaleString()
                                        : 'Never'}
                                </td>
                                <td style={styles.td}>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            style={styles.actionBtn}
                                            onClick={() => handleToggleStatus(client.id)}
                                            title={client.isActive ? 'Deactivate' : 'Activate'}
                                        >
                                            {client.isActive
                                                ? <ToggleRight size={22} color="#22c55e" />
                                                : <ToggleLeft size={22} color="#94a3b8" />
                                            }
                                        </button>
                                        <button
                                            style={styles.actionBtn}
                                            onClick={() => handleDelete(client.id)}
                                            title="Delete"
                                        >
                                            <Trash2 size={18} color="#ef4444" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {showAddModal && (
                <div style={styles.modal} onClick={() => setShowAddModal(false)}>
                    <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <h2 style={styles.modalTitle}>Add New Client</h2>
                        <form onSubmit={handleAddClient}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569' }}>
                                Client Name *
                            </label>
                            <input
                                style={styles.input}
                                type="text"
                                placeholder="Enter client name"
                                value={newClientName}
                                onChange={e => setNewClientName(e.target.value)}
                                autoFocus
                            />
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569' }}>
                                S2S Token *
                            </label>
                            <input
                                style={styles.input}
                                type="text"
                                placeholder="Enter S2S token provided by admin"
                                value={newClientToken}
                                onChange={e => setNewClientToken(e.target.value)}
                            />
                            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px' }}>
                                Enter the S2S token that will be used for API authentication.
                            </p>
                            <div style={styles.modalButtons}>
                                <button type="button" style={styles.cancelBtn} onClick={() => setShowAddModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" style={styles.submitBtn} disabled={submitting}>
                                    {submitting ? 'Creating...' : 'Create Client'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageClients;
